import {
  color,
  Flex,
  PrimaryText,
  SecondaryHeading,
  SecondaryHeadingSize,
  spacing,
  StyleProps,
  useColorMode,
  useIsMobileView,
} from 'deepstash-ui';
import React, { useMemo } from 'react';
import IdeaCounter from 'src/components/counter/IdeaCounter';
import { ADD_SOURCE, ArticleStatus } from 'types';
import { HeadingSize } from 'types/globalTypes';
import { Source } from 'types/models';
import pageStrings from 'utils/strings/pageStrings';
import { SourceTitleByTypeSize } from './SourceTitleByType';

export interface BaseSourceTitleProps extends StyleProps {
  leftItem?: React.ReactElement;
  size?: SourceTitleByTypeSize;
  mainTitle: React.ReactChild;
  subTitle?: React.ReactChild;
  source?: Source;
  titleHeadingSize: 'p' | HeadingSize;
  showProgress?: boolean;
}

const BaseSourceTitle: React.FC<BaseSourceTitleProps> = ({
  leftItem,
  mainTitle,
  subTitle,
  size = 'lg',
  titleHeadingSize,
  noOfLines,
  source,
  ...props
}) => {
  const { colorMode } = useColorMode();
  const isMobileView = useIsMobileView();

  const { headingTextSize, titleMarginTop, titleMarginLeft } = useMemo(() => {
    switch (size) {
      case 'sm': {
        return {
          headingTextSize: {
            textSize: 's' as SecondaryHeadingSize,
            fontSize: '1rem',
            lineHeight: '1.25rem',
          },
          titleMarginTop: spacing.toRem(1),
          titleMarginLeft: spacing.toRem(12),
        };
      }
      case 'md': {
        return {
          headingTextSize: {
            textSize: 's' as SecondaryHeadingSize,
            fontWeight: 800,
          },
          titleMarginTop: spacing.toRem(0),
          titleMarginLeft: spacing.toRem(16),
        };
      }
      default: {
        return {
          headingTextSize: {
            textSize: (isMobileView ? 's' : 'm') as SecondaryHeadingSize,
          },
          titleMarginTop: spacing.toRem(4),
          titleMarginLeft: spacing.toRem(16),
        };
      }
    }
  }, [isMobileView, size]);

  const showPlaceholder = mainTitle === '';

  return (
    <Flex
      mb={{ base: spacing.XS.rem, lg: 'unset' }}
      mt={spacing.M.rem}
      w={isMobileView ? 'calc(100% - 1rem)' : '100%'}
      mx="0.5rem"
      gridGap={spacing.XXS.rem}
      {...props}
    >
      {leftItem}
      <Flex
        flexDir="column"
        mt={titleMarginTop}
        justifyContent="center"
        ml={leftItem ? titleMarginLeft : 0}
        alignItems={props.alignItems ?? 'flex-start'}
        w="100%"
        textAlign={props.textAlign}
        zIndex={1}
      >
        {titleHeadingSize === 'p' ? (
          <PrimaryText
            {...headingTextSize}
            fontWeight={700}
            lineHeight="1.255em"
            color={
              showPlaceholder
                ? color[colorMode].textDisabled
                : color[colorMode].text
            }
            noOfLines={noOfLines}
            zIndex={1}
            w="100%"
            wordBreak="break-word"
            mb={spacing.XS.rem}
          >
            {showPlaceholder ? pageStrings[ADD_SOURCE].addTitle : mainTitle}
          </PrimaryText>
        ) : (
          <SecondaryHeading
            {...headingTextSize}
            fontWeight={700}
            lineHeight="1.255em"
            color={
              showPlaceholder
                ? color[colorMode].textDisabled
                : color[colorMode].text
            }
            noOfLines={noOfLines}
            zIndex={1}
            w="100%"
            size={titleHeadingSize}
            wordBreak="break-word"
            mb={spacing.XS.rem}
          >
            {showPlaceholder ? pageStrings[ADD_SOURCE].addTitle : mainTitle}
          </SecondaryHeading>
        )}
        {subTitle}
        {(source?.ideaCount ?? source?.ideas.length ?? 0) > 0 && (
          <IdeaCounter
            ideaCount={source?.ideaCount ?? source?.ideas.length ?? 0}
            totalReadCount={
              source?.status === ArticleStatus.PUBLISHED
                ? source?.totalReads
                : undefined
            }
            mt={spacing.XS.rem}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default BaseSourceTitle;
